import * as moment from 'moment-timezone';
import {BasicEntity, BasicProperty} from "../../basic-entity-back/annotations";
import {Cliente} from "../personas/cliente.model";
import {LineaPedido} from "./linea-pedido.model";
import {PropertyType} from "../../basic-entity-back/property-type/property-type";
import {NestedBehavior} from "../../basic-entity-back/property-type/nested-model-type";
import {ReadWrite} from "../../basic-entity-back/basic-entity-interface/mapping-external";
import {BaseEntity} from "../base-entity.model";
import {ExactSearchFilter, PartialSearchFilter} from "../../basic-entity-back/filters/search-filter";
import {EstadoPedidoClienteFilter} from "../../basic-entity-back/filters/estado-pedido-cliente-filter";
import {
    agrupadoCliente, ESTADOS_PEDIDO_CLIENTE, opcionesEstadoCliente, TEXTO_ENVIADO, TEXTO_PAUSADO, textoDeAgrupado
} from "./estados-pedido-cliente";
import {MetodoFabricacion} from "../materiales/metodo-fabricacion.model";
import {Material} from "../materiales/material.model";
import {Albaran} from "../documentacion/albaran.model";
import {TransportistaTipoEnvioRel} from "../envios/transportista-tipo-envio-rel.model";

@BasicEntity({
    name: 'Pedidos',
    endpoint: 'pedidos',
    modelNameProperty: 'resumen',
    isPaginated: true,
    // Sin caché del manager: los pedidos de un cliente no paran de crecer. Con `Lazy`, resolver el
    // nombre de cualquier Uri a un pedido (`pedidoRepetido` en el listado, que be-table precarga
    // aunque no se enseñe) descargaba la colección entera con findAndFollow. Así se piden solo esos
    // IRIs con `iri-search[]` (BulkUriFilter en la API).
    acceptsBulkIriSearch: true,
    // Un cliente solo ve sus propios pedidos: filtrar por cliente en el panel no tiene sentido
    sinFiltroEnElPanel: ['cliente']
})
export class Pedido extends BaseEntity {
    static readonly ESTADOS_PEDIDO = {
        // PENDIENTE: 'Pendiente',
        // CONFIRMADO: "Confirmado",
        // VALIDADO: 'Validado',
        // EN_PRODUCCION: 'En producción',
        // FINALIZADO: 'Finalizado',
        // ENVIADO: 'En tránsito',
        // EN_REPARTO: 'En reparto',
        // CANCELADO: 'Cancelado'
        EN_CURSO: "En curso",
        REALIZADO: "Subido a CNC",
        RECHAZADO: "Rechazado",
        PENDIENTE_VALIDAR: "Pendiente de validación",
        VALIDADO: "Validado",
        EN_PRODUCCION: "En producción",
        EN_LOGISTICA: "En logística",
        LISTO_PARA_ENVIO: "Listo para envío",
        EN_TRANSITO: "En tránsito",
        ENTREGADO: "Entregado",
        CANCELADO: "Cancelado"
    };

    /**
     * Lo que el cliente ve como EN PRODUCCIÓN (sale de la tabla única de estados-pedido-cliente.ts). Un estado
     * parcial lo da la línea más avanzada, y las demás pueden seguir en fábrica, así que los parciales de logística,
     * listo para envío y en tránsito siguen en producción: Listo para envío y ENVIADO solo con todas las líneas ahí.
     */
    static readonly ESTADOS_CLIENTE_EN_PRODUCCION: string[] = ESTADOS_PEDIDO_CLIENTE
        .filter(f => f.agrupado === 'EN PRODUCCIÓN')
        .map(f => f.estado);

    /** Lo que ve el cliente mientras CNC le pregunta por alguna pieza del pedido. */
    static readonly TEXTO_PAUSADO = TEXTO_PAUSADO;

    /** Lo que ve el cliente de un pedido En tránsito o Entregado: el detalle va en la columna Envío */
    static readonly TEXTO_ENVIADO = TEXTO_ENVIADO;

    /**
     * El estado tal como lo ve el cliente (listado de pedidos, ficha de mensajes): validado cuenta como subido, lo
     * que está en fábrica como en producción, en tránsito y entregado como enviado, sin «Parcialmente» y en
     * mayúsculas (tabla en estados-pedido-cliente.ts, la misma que usa el filtro de Estado). Con alguna pieza
     * pausada (`pausado`, lo calcula la API) manda la pausa, sea cual sea el estado.
     */
    static textoEstadoCliente(estado: string, pausado = false): string {
        return textoDeAgrupado(Pedido.estadoAgrupadoCliente(estado, pausado));
    }

    /**
     * Como textoEstadoCliente, pero sin juntar En tránsito y Entregado en ENVIADO. Es lo que da el color de la
     * píldora: el texto dice ENVIADO, pero En tránsito sigue en gris y Entregado en verde.
     */
    static estadoAgrupadoCliente(estado: string, pausado = false): string {
        return pausado ? TEXTO_PAUSADO : agrupadoCliente(estado);
    }

    /**
     * Las opciones del filtro de Estado del listado: los textos de la píldora (EN CURSO, SUBIDO A CNC, EN PRODUCCIÓN,
     * LISTO PARA ENVÍO, ENVIADO, RECHAZADO), cada uno una vez; PENDIENTE DE VALIDACIÓN, VALIDADO y CANCELADO no se
     * ofrecen (TEXTOS_SIN_OPCION_DE_FILTRO). EstadoPedidoClienteFilter traduce cada opción a los estados reales que
     * se ven así. PAUSADO no es una opción: no es un estado (un pausado sale en la de su estado real).
     */
    static opcionesFiltroEstado(): string[] {
        return opcionesEstadoCliente();
    }

    @BasicProperty({
        isId: true,
        name: 'ID',
        type: PropertyType.Integer()
    })
    private _id: number;

    @BasicProperty({
        name: 'Cliente',
        type: PropertyType.NestedModel(Cliente, null, NestedBehavior.SendIriOnly),
        filters: [ExactSearchFilter]
    })
    private _cliente: Cliente;

    @BasicProperty({
        name: 'Líneas',
        keyInApi: 'lineasPedido',
        type: PropertyType.NestedModel(LineaPedido, 'pedido', NestedBehavior.CreateOnly),
        array: true
    })
    private _lineas: LineaPedido[];

    @BasicProperty({
        name: 'Estado',
        // Solo cambian las opciones del filtro (Choice no comprueba el valor que llega de la API):
        // el estado sigue siendo el real
        type: PropertyType.Choice(...Pedido.opcionesFiltroEstado()),
        sortable: true,
        filters: [EstadoPedidoClienteFilter]
    })
    private _estado;

    /** El número de pedido que ve el usuario: es el id, pero con clave propia porque la columna `id`
     * no se muestra en ninguna tabla */
    @BasicProperty({
        name: 'Nº de pedido',
        type: PropertyType.Integer(),
        dontReadFromApi: true,
        readWrite: ReadWrite.ReadOnly
    })
    get numeroPedido(): number {
        return this.id;
    }

    @BasicProperty({
        name: 'Nº de archivos',
        type: PropertyType.Integer(),
        dontReadFromApi: true,
        readWrite: ReadWrite.ReadOnly
    })
    get numeroArchivos(): number {
        return this.lineas.length;
    }

    @BasicProperty({
        name: 'Codigos de paciente',
        keyInApi: 'lineasPedido.codigoPaciente',
        type: PropertyType.String(),
        nullable: true,
        dontReadFromApi: true,
        filters: [PartialSearchFilter]
    })
    private _codigosPaciente;

    /**
     * Fecha de envío estimada del pedido: la más tardía de las fechas de salida de sus
     * líneas (la última en salir es la que marca cuándo está el pedido completo fuera).
     * Se ignoran las líneas rechazadas o canceladas, que no se llegan a enviar.
     *
     * "Estimada" en el nombre a propósito: mientras la línea no se valida esto es una
     * estimación, y al validarla se recalcula tomando ese momento como referencia. Si la
     * validación se retrasa -aunque sea unas horas, cruzando la hora de corte- la fecha se
     * mueve hacia adelante, y el cliente veía cambiar a peor algo que le habíamos presentado
     * como un dato firme.
     */
    @BasicProperty({
        name: 'Fecha estimada de envío',
        type: PropertyType.Date(),
        nullable: true,
        dontReadFromApi: true,
        readWrite: ReadWrite.ReadOnly
    })
    get fechaSalida() {
        const fechas = (this._lineas ?? [])
            .filter(l => l.estado !== LineaPedido.ESTADOS_LINEA_PEDIDO.RECHAZADO
                && l.estado !== LineaPedido.ESTADOS_LINEA_PEDIDO.CANCELADO)
            .map(l => l.fechaSalida)
            .filter(f => !!f);
        return fechas.length ? fechas.reduce((masTardia, f) => f.isAfter(masTardia) ? f : masTardia) : null;
    }

    @BasicProperty({
        name: 'Métodos de fabricación y materiales',
        type: PropertyType.String(),
        dontReadFromApi: true,
        readWrite: ReadWrite.ReadOnly
    })
    get metodosFabricacionMateriales(): string {
        return this._lineas.map(l => l.metodoFabricacion ? (l.metodoFabricacion.descripcion + ' (' + l.material?.descripcion + ')') : '').join(', ');
    }

    @BasicProperty({
        name: 'Método de fabricación',
        keyInApi: 'lineasPedido.metodoFabricacion',
        type: PropertyType.Uri(MetodoFabricacion),
        nullable: true,
        dontReadFromApi: true,
        filters: [ExactSearchFilter]
    })
    private _metodosFabricacion;


    @BasicProperty({
        name: 'Material',
        keyInApi: 'lineasPedido.material',
        type: PropertyType.Uri(Material),
        nullable: true,
        dontReadFromApi: true,
        filters: [ExactSearchFilter]
    })
    private _materiales;

    @BasicProperty({
        name: 'Diseño analógico asociado',
        type: PropertyType.Object(),
        readWrite: ReadWrite.ReadOnly
    })
    private _solicitud;

    @BasicProperty({
        name: 'Blender asociado',
        type: PropertyType.Object(),
        readWrite: ReadWrite.ReadOnly
    })
    private _blender;

    @BasicProperty({
        name: 'Es repetición',
        type: PropertyType.Uri(Pedido),
        readWrite: ReadWrite.ReadOnly
    })
    private _pedidoRepetido;

    /**
     * Lo calcula la API: alguna línea del pedido está fabricada, con lote y en un albarán. El
     * criterio vive solo en el servidor, así que aquí no se reconstruye recorriendo las líneas.
     */
    @BasicProperty({
        name: 'Puede descargar guías de fabricación',
        type: PropertyType.Boolean(),
        readWrite: ReadWrite.ReadOnly
    })
    private _puedeDescargarGuiasFabricacion: boolean;

    /**
     * Lo calcula la API: alguna pieza está pausada mientras CNC consulta al cliente. Ni el motivo
     * ni quién la pausó llegan aquí.
     */
    @BasicProperty({
        name: 'Pausado',
        type: PropertyType.Boolean(),
        readWrite: ReadWrite.ReadOnly
    })
    private _pausado: boolean;

    @BasicProperty({
        name: 'Motivo de rechazo',
        type: PropertyType.String(),
        nullable: true,
        readWrite: ReadWrite.ReadOnly
    })
    private _motivoRechazo = null;

    @BasicProperty({
        name: 'Transportista - Tipo envío',
        type: PropertyType.NestedModel(TransportistaTipoEnvioRel, null, NestedBehavior.SendIriOnly),
        order: 3
    })
    private _tipoEnvioTransportista;

    @BasicProperty({
        name: 'Tracking',
        type: PropertyType.String(),
        readWrite: ReadWrite.ReadOnly,
    })
    private _urlTracking;

    /**
     * Lo calcula la API (Pedido::getEstadoEnvio) con el mismo envío que da `urlTracking`: «Sin
     * envío», «En tránsito», «Entregado», «Entregado en mano» o «Anulado». Nunca llega null.
     */
    @BasicProperty({
        name: 'Envío',
        type: PropertyType.String(),
        readWrite: ReadWrite.ReadOnly,
    })
    private _estadoEnvio: string;

    /**
     * Lo calcula la API (Pedido::getFechaEstadoEnvio), del mismo envío que `estadoEnvio`: salida
     * (En tránsito, Entregado en mano), entrega o anulación según el transportista. Null si no hay
     * una fecha de fiar (Sin envío, Entregado a mano).
     */
    @BasicProperty({
        name: 'Fecha del envío',
        type: PropertyType.DateTime(),
        nullable: true,
        readWrite: ReadWrite.ReadOnly,
    })
    private _fechaEstadoEnvio: moment.Moment = null;

    get resumen() {
        return 'Pedido ' + this.id
    }

    get id(): number {
        return this._id;
    }

    set id(value: number) {
        this._id = value;
    }

    get cliente(): Cliente {
        return this._cliente;
    }

    set cliente(value: Cliente) {
        this._cliente = value;
    }

    get lineas(): LineaPedido[] {
        return this._lineas;
    }

    set lineas(value: LineaPedido[]) {
        this._lineas = value;
    }

    get estado() {
        return this._estado;
    }

    set estado(value) {
        this._estado = value;
    }

    get codigosPaciente() {
        return this._lineas ? this.lineas.map(l => l.codigoPaciente).join(', ') : '';
    }

    set codigosPaciente(value) {
        this._codigosPaciente = value;
    }

    get metodosFabricacion() {
        return this._metodosFabricacion;
    }

    set metodosFabricacion(value) {
        this._metodosFabricacion = value;
    }

    get materiales() {
        return this._materiales;
    }

    set materiales(value) {
        this._materiales = value;
    }

    get solicitud() {
        return this._solicitud;
    }

    set solicitud(value) {
        this._solicitud = value;
    }

    get pedidoRepetido() {
        return this._pedidoRepetido ? this._pedidoRepetido : '';
    }

    set pedidoRepetido(value) {
        this._pedidoRepetido = value;
    }

    get motivoRechazo(): any {
        return this._motivoRechazo;
    }

    set motivoRechazo(value: any) {
        this._motivoRechazo = value;
    }

    get blender() {
        return this._blender;
    }

    set blender(value) {
        this._blender = value;
    }

    get tipoEnvioTransportista() {
        return this._tipoEnvioTransportista;
    }

    set tipoEnvioTransportista(value) {
        this._tipoEnvioTransportista = value;
    }


    get urlTracking() {
        return this._urlTracking;
    }

    set urlTracking(value) {
        this._urlTracking = value;
    }

    get estadoEnvio(): string {
        return this._estadoEnvio;
    }

    set estadoEnvio(value: string) {
        this._estadoEnvio = value;
    }

    get fechaEstadoEnvio(): moment.Moment {
        return this._fechaEstadoEnvio;
    }

    set fechaEstadoEnvio(value: moment.Moment) {
        this._fechaEstadoEnvio = value;
    }

    get puedeDescargarGuiasFabricacion(): boolean {
        return this._puedeDescargarGuiasFabricacion;
    }

    set puedeDescargarGuiasFabricacion(value: boolean) {
        this._puedeDescargarGuiasFabricacion = value;
    }

    get pausado(): boolean {
        return this._pausado;
    }

    set pausado(value: boolean) {
        this._pausado = value;
    }
}

LineaPedido.MAP_PEDIDO.type = PropertyType.Uri(Pedido);
Albaran.MAP_PEDIDO.type = PropertyType.Uri(Pedido);
